<template>
  <template v-if="showAssetField || (isLoggedInOnSupplier && isNew)">
    <v-row density="compact">
      <v-col
        v-if="isLoggedInOnSupplier && !hasInitialOrganization && isNew"
        cols="12"
        :sm="showAssetField ? 6 : 12"
        class="text-body-medium"
      >
        <div>{{ $t("tickets.field.organization") }} *</div>
        <v-autocomplete
          v-model="internalValue.organization"
          :items="organizations!"
          :loading="organizationStatus === 'pending'"
          :placeholder="$t('tickets.placeholder.organization')"
          :rules="[(val) => !!val || $t('validation.required')]"
          item-title="description"
          item-value="_id"
          menu-icon="arrow_drop_down"
          @update:model-value="onOrganizationUpdated"
        />
      </v-col>

      <v-col
        v-if="showAssetField"
        cols="12"
        :sm="isLoggedInOnSupplier && !hasInitialOrganization && isNew ? 6 : 12"
        class="text-body-medium"
      >
        <div>{{ $t("asset") }} *</div>
        <asset-select
          :model-value="internalValue.asset?._id"
          :organization-id="internalValue.organization"
          @update:asset="(value) => (internalValue.asset = value)"
        />
      </v-col>
    </v-row>
  </template>

  <v-row density="compact">
    <v-col class="text-body-medium">
      <div>{{ $t("tickets.form.description") + (batchEdit ? "" : " *") }}</div>
      <v-combobox
        :model-value="internalValue.description"
        :clearable="!isReadOnly"
        :disabled="!hasCreateOrUpdatePermission"
        :items="descriptionOptions.map((opt) => opt.description)"
        :placeholder="$t('tickets.placeholder.description')"
        :loading="descriptionsPending || pending"
        :menu-icon="isReadOnly ? '' : 'arrow_drop_down'"
        :readonly="isReadOnly"
        :rules="isReadOnly ? [] : [(val) => batchEdit || !!val || $t('validation.required')]"
        @update:model-value="debounceDescription"
      />
    </v-col>
  </v-row>

  <v-row density="compact">
    <v-col class="text-body-medium">
      <div>{{ $t("tickets.field.description") }}</div>
      <v-textarea
        :disabled="!hasCreateOrUpdatePermission"
        :placeholder="$t('tickets.placeholder.remark')"
        :loading="pending"
        :model-value="internalValue.remark"
        :readonly="isReadOnly"
        @update:model-value="debounceRemark"
      />
    </v-col>
  </v-row>

  <v-row density="compact">
    <v-col cols="12" sm="6" class="text-body-medium">
      <div>{{ $t(`${ticketType?.toLowerCase()}s.field.date`) }}</div>
      <v-text-field
        v-model="internalValue.date"
        :clearable="!isReadOnly && !isTicketFinished"
        :disabled="!hasCreateOrUpdatePermission"
        :hint="!isReadOnly && isTicketFinished ? $t('tickets.field.dateHint') : undefined"
        :placeholder="$t('tickets.placeholder.date')"
        :loading="pending"
        :readonly="isReadOnly || isTicketFinished"
        type="date"
      />
    </v-col>

    <v-col cols="12" sm="6" class="text-body-medium">
      <div>{{ $t("tickets.field.tags") }}</div>
      <v-autocomplete
        v-if="!hideTag"
        v-model="internalValue.tags"
        :disabled="!hasCreateOrUpdatePermission"
        :items="ticketTagOptions"
        :placeholder="$t('tickets.placeholder.tags')"
        :loading="pending || ticketTagsStatus === 'pending'"
        :menu-icon="isReadOnly ? '' : 'arrow_drop_down'"
        :readonly="isReadOnly"
        item-title="description"
        item-value="_id"
        multiple
        return-object
      >
        <template #selection="{ item, index }">
          <tag-badge v-if="index < 2" :tag="item" />
          <span v-else-if="index === 2" class="text-body-small align-self-center">(+{{ (internalValue.tags?.length ?? 0) - 2 }})</span>
        </template>

        <template #item="{ props: itemProps, item }">
          <v-list-item v-bind="itemProps">
            <template #title>
              <tag-badge :tag="item" />
            </template>
          </v-list-item>
        </template>
      </v-autocomplete>
    </v-col>
  </v-row>

  <floating-card-header :title="$t('tickets.assignees')" icon="$supplier" />

  <v-row density="compact">
    <v-col cols="12" sm="6" class="text-body-medium">
      <div>{{ $t("tickets.field.supplier") + (batchEdit ? "" : " *") }}</div>
      <form-organization-select
        v-model="selectedSupplierId"
        :clearable="!isReadOnly"
        :disabled="!hasCreateOrUpdatePermission || !isSupplierAllowedToChangeOrganization"
        :loading="suppliersPending || pending"
        :menu-icon="isReadOnly ? '' : 'arrow_drop_down'"
        :organizations="supplierOptions ?? []"
        :placeholder="$t('tickets.placeholder.supplier')"
        :readonly="isReadOnly || !isSupplierAllowedToChangeOrganization"
        :rules="isReadOnly ? [] : [(val) => batchEdit || !!val || $t('validation.required')]"
        class="mt-1 small-field-text"
        item-title="description"
        @update:model-value="
          () => {
            internalValue.users = []
            internalValue.contract = undefined
            internalValue.articles = undefined
          }
        "
      />
    </v-col>

    <v-col cols="12" sm="6" class="text-body-medium">
      <div>{{ $t("tickets.field.user") }}</div>
      <v-autocomplete
        :clearable="!isReadOnly"
        :disabled="!hasCreateOrUpdatePermission || !isSupplierAllowedToChangeOrganization"
        :hint="!readonly && !usersPending && !userOptions?.length ? $t('noUsersUnderSupplier') : ''"
        :items="userOptions!"
        :loading="usersPending || pending"
        :menu-icon="isReadOnly ? '' : 'arrow_drop_down'"
        :model-value="internalValue.users?.[0]"
        :placeholder="$t('tickets.placeholder.user')"
        :readonly="isReadOnly || !isSupplierAllowedToChangeOrganization"
        class="mt-1 small-field-text"
        item-title="description"
        item-value="value"
        @update:model-value="(user) => (internalValue.users = user ? [user] : [])"
      />
    </v-col>
  </v-row>

  <floating-card-header :title="$t('finance.title')" icon="euro" />

  <v-row density="compact">
    <v-col cols="12" sm="6" class="text-body-medium">
      <ticket-contract-selection
        v-if="hasOrganization && isSupplierSet"
        v-model="internalValue.contract"
        :ticket="{ ...internalValue, type: ticketType }"
        :ticket-type="ticketType"
        :label="$t('finance.contracts.title') + (contractIsMandatory ? ' *' : '')"
        :rules="isReadOnly ? [] : [(val) => !contractIsMandatory || !!val || $t('validation.required')]"
        :contract-required="contractIsMandatory"
        :readonly="isReadOnly || !isSupplierAllowedToChangeContract"
        :disabled="!isSupplierSet || !hasCreateOrUpdatePermission || !isSupplierAllowedToChangeContract"
        :is-new="isNew"
        :asset-ids-override="assetIds"
        class="mb-4"
      />

      <template v-else>
        <div class="text-body-medium">{{ $t("tickets.field.contract") }}</div>
        <v-select disabled :placeholder="$t('tickets.placeholder.contract')" />
      </template>
    </v-col>

    <v-col cols="12" sm="6" class="text-body-medium">
      <div>{{ $t("tickets.field.invoicePeriod") }}</div>
      <v-autocomplete
        v-model="internalValue.invoicePeriod"
        :clearable="!isReadOnly"
        :disabled="
          (!internalValue.invoicePeriod && !readonly && !invoicePeriodsPending && !invoicePeriods?.length) || !hasCreateOrUpdatePermission
        "
        :items="invoicePeriods"
        :loading="invoicePeriodsPending || pending"
        :menu-icon="isReadOnly ? '' : 'arrow_drop_down'"
        :placeholder="$t('tickets.placeholder.invoicePeriod')"
        :readonly="isReadOnly"
        item-title="description"
        item-value="description"
        style="align-self: flex-start"
      />
    </v-col>
  </v-row>

  <slot
    name="contract-articles"
    :contract="internalValue.contract"
    :error-messages="!pending && internalValue.contract ? [$t('invalidContract')] : undefined"
  />

  <slot name="articles" />
</template>

<script setup lang="ts">
import cloneDeep from "lodash-es/cloneDeep"
import debounce from "lodash-es/debounce"
import isEqual from "lodash-es/isEqual"
import pick from "lodash-es/pick"
import sortBy from "lodash-es/sortBy"

const emit = defineEmits(["update:modelValue"])
const props = defineProps<{
  modelValue: Partial<Ticket>
  ticketType: TicketType
  assetIds?: Array<string>
  hideTag?: boolean
  showAssetField?: boolean
  readonly?: boolean
  batchEdit?: boolean
  pending?: boolean
}>()

const { modelValue, ticketType, showAssetField, readonly, batchEdit, pending, assetIds } = toRefs(props)

const authStore = useAuthStore()
const organizationStore = useOrganizationStore()
const userStore = useUserStore()
const invoicePeriodStore = useInvoicePeriodStore()
const ticketTagStore = useTicketTagStore()
const ticketStore = useTicketStore()
const supplierStore = useSupplierStore()

const { isSupplier, currentOrganization: currentOrganizationId, sourceOrganization } = storeToRefs(authStore)
const { currentUser } = storeToRefs(userStore)
const { inspectionTicketTypes } = storeToRefs(ticketStore)
const { isLoggedInOnSupplier } = storeToRefs(supplierStore)

const { hasScope } = authStore

const { getDescriptionListForTicketType } = useTicketLists()

const internalValue = ref<Partial<Ticket>>({
  ...cloneDeep(modelValue.value),
})

const hasInitialOrganization = !!modelValue.value.organization
const isSupplierSet = computed(() => !!internalValue.value.supplier)
const selectedSupplierOrganizationId = computed(() => internalValue.value.supplier?._id)

const contractIsMandatory = ref(false)
const updateContractIsMandatory = async () => {
  if (!selectedSupplierOrganizationId.value) {
    contractIsMandatory.value = false
    return
  }

  try {
    const settings = await organizationStore.getPublicSettings(selectedSupplierOrganizationId.value)
    contractIsMandatory.value = settings.contractIsMandatory ?? false
  } catch (error) {
    console.error(`Failed to fetch organization settings for organization ${selectedSupplierOrganizationId.value}`, error)
    contractIsMandatory.value = false
  }
}
watch(selectedSupplierOrganizationId, updateContractIsMandatory, { immediate: true })

const selectedSupplierId = computed({
  get: () => internalValue.value.supplier?._id,
  set: (value: string | undefined | null) => {
    if (!value) {
      internalValue.value.supplier = undefined
      return
    }

    const selectedSupplier = supplierOptions.value?.find((opt) => opt._id === value)
    internalValue.value.supplier = pick(selectedSupplier, ["_id", "description"]) as Pick<Organization, "_id" | "description">
  },
})
const organizationHasOwnershipOverTicket = computed(() => {
  if (!currentUser.value || !internalValue.value.organization) return false
  // if no supplier is selected, the ticket is new
  if (!internalValue.value.supplier) return true
  if (currentUser.value.userRole === UserRole.SUPER_ADMIN) return true
  // if the user has normal admin rights, they can bypass the ownership check.
  if (hasScope(internalValue.value.organization, AuthScope.ADMIN)) return true

  return (
    internalValue.value.organization === currentUser.value.organization ||
    internalValue.value.supplier?._id === currentUser.value.organization
  )
})

const isNew = computed(() => !internalValue.value._id && !batchEdit.value)
const hasOrganization = computed(() => !!internalValue.value.organization)
const hasCreateOrUpdatePermission = computed(() => {
  if (!hasOrganization.value) return false

  const hasPermission = (scope: AuthScope) => hasScope(internalValue.value.organization!, scope)

  switch (ticketType.value) {
    case TicketType.ACTION:
      return isNew.value ? hasPermission(AuthScope.CAN_CREATE_ACTIONS) : hasPermission(AuthScope.CAN_UPDATE_ACTIONS)
    case TicketType.NEN3140:
    case TicketType.NEN1010:
    case TicketType.INSPECTION:
      return isNew.value ? hasPermission(AuthScope.CAN_CREATE_INSPECTIONS) : hasPermission(AuthScope.CAN_UPDATE_INSPECTIONS)
    case TicketType.MALFUNCTION:
      return isNew.value ? hasPermission(AuthScope.CAN_CREATE_MALFUNCTIONS) : hasPermission(AuthScope.CAN_UPDATE_MALFUNCTIONS)
    default:
      return false
  }
})

const isReadOnly = computed(() => {
  // Disable fields when readonly is set,
  // the user is missing permissions
  // or organization is not set (ex: supplier with multiple organizations)
  return readonly.value || !hasCreateOrUpdatePermission.value || !hasOrganization.value || !organizationHasOwnershipOverTicket.value
})

// When the supplier id is the same as the organization we know this ticket belongs to the organization and not a supplier
const isOrganizationTicket = computed(
  () => !!internalValue.value.organization && internalValue.value.supplier?._id === internalValue.value.organization,
)

// When the contract is set by the organization, the supplier should not be able to change it
const isSupplierAllowedToChangeContract = computed(() => !isSupplier.value || !isOrganizationTicket.value)

// When the supplier and user are set by the organization, the supplier should not be able to change it
const isSupplierAllowedToChangeOrganization = computed(() => !isSupplier.value || !isSupplierSet.value || !isOrganizationTicket.value)

const isTicketFinished = computed(() => {
  const status = internalValue.value.status
  return status && (postProcessTicketStatuses as ReadonlyArray<string>).includes(status)
})

const { data: organizations, status: organizationStatus } = useLazyAsyncData(
  `organizations:${isSupplier.value ? sourceOrganization.value : "suppliers"}`,
  async () => {
    const organizations = isSupplier.value
      ? await organizationStore.getOrganizationsForSupplier(sourceOrganization.value!)
      : await organizationStore.getOrganizations()

    const sortedOrganizations = sortBy(organizations, "description")

    // If supplier, only return the organizations for which the supplier has create permissions
    if (isSupplier.value) {
      const scope =
        ticketType.value === TicketType.ACTION
          ? AuthScope.CAN_CREATE_ACTIONS
          : inspectionTicketTypes.value.includes(ticketType.value)
            ? AuthScope.CAN_CREATE_INSPECTIONS
            : AuthScope.CAN_CREATE_MALFUNCTIONS

      return sortedOrganizations.filter((org) => hasScope(org._id, scope))
    }

    return sortedOrganizations
  },
  {
    default: () => [] as Array<Organization>,
    watch: [isSupplier],
  },
)

const {
  data: descriptionOptions,
  pending: descriptionsPending,
  refresh: refreshDescriptions,
} = useLazyAsyncData(
  `descriptions-${ticketType.value?.toLocaleLowerCase()}`,
  async () => {
    if (isReadOnly.value || !hasOrganization.value) return []

    return getDescriptionListForTicketType(ticketType.value, internalValue.value.organization)
  },
  { default: () => [] as Array<ListValue>, watch: [hasOrganization, isReadOnly, ticketType] },
)

const { data: ticketTagOptions, status: ticketTagsStatus } = useAsyncData(
  `ticket-tags:${currentOrganizationId.value}:${ticketType.value}`,
  async () => {
    if (isReadOnly.value) return []
    return ticketTagStore.getTicketTags(false, currentOrganizationId.value)
  },
  { default: () => [] as Array<TicketTag>, watch: [isReadOnly, ticketType] },
)

const {
  data: supplierOptions,
  pending: suppliersPending,
  refresh: refreshSuppliers,
} = useLazyAsyncData(
  "supplier-options",
  async () => {
    if (isReadOnly.value) {
      const selectedOrganization = organizations.value.find((organization) => organization._id === internalValue.value.supplier?._id)

      if (!selectedOrganization) return []

      return [pick(selectedOrganization, ["_id", "icon", "type", "description", "domain"])]
    }

    const organization = internalValue.value.organization ?? currentOrganizationId.value
    if (!organization) return []

    const [supplierScopes, currentOrganization] = await Promise.all([
      organizationStore.getSuppliersWithScopeSelection(organization),
      organizationStore.getOrganization(organization),
    ])

    if (supplierScopes) {
      const options = sortBy(
        supplierScopes.map((template) => pick(template.supplier, ["_id", "icon", "type", "description", "domain"])),
        "description",
      )

      if (currentOrganization && !isSupplier.value) {
        // Add organization to the top of the list
        options.unshift(pick(currentOrganization, ["_id", "icon", "type", "description", "domain"]))
      } else if (currentOrganization && isSupplier.value) {
        // Check if current organization has a supplier that corresponds with the organization of the current user
        const supplierTemplate = supplierScopes.find(({ supplier }) => supplier._id === currentUser.value?.organization)
        if (supplierTemplate) {
          return [pick(supplierTemplate.supplier, ["_id", "icon", "type", "description", "domain"])]
        } else {
          return []
        }
      }

      return options
    }
  },
  {
    default: () => [] as Array<Pick<Organization, "_id" | "icon" | "type" | "description" | "domain">>,
    watch: [isReadOnly],
  },
)

const {
  data: userOptions,
  pending: usersPending,
  refresh: refreshInspectors,
} = useLazyAsyncData(
  "user-options",
  async () => {
    if (isReadOnly.value) return []
    if (!internalValue.value.organization || !internalValue.value.supplier) {
      return []
    }

    // in order for suppliers to also get the right results, we have to manually check the scope, instead of just calling the `scopes` array.
    // this is because the `scopes` array is based on the current user's organization, and not the supplier's organization.
    const canAssignOtherUsers = await authStore.checkScope(
      currentUser.value!._id,
      AuthScope.CAN_ASSIGN_USERS,
      internalValue.value.supplier!._id,
    )
    const isFromSelectedSupplier = currentUser.value?.organization === internalValue.value.supplier?._id
    const isFromTicketOrganization = currentUser.value?.organization === internalValue.value.organization

    const supplierUserCannotAssign = canAssignOtherUsers.allowed === false && !authStore.isSuperAdmin
    const customerUserCannotAssign = !hasScope(internalValue.value.organization!, AuthScope.CAN_ASSIGN_USERS)

    const isSupplierAssignmentDisallowed = !isFromTicketOrganization && isFromSelectedSupplier && supplierUserCannotAssign
    const isCustomerAssignmentDisallowed = isFromTicketOrganization && isFromSelectedSupplier && customerUserCannotAssign

    if (isSupplierAssignmentDisallowed || isCustomerAssignmentDisallowed) {
      return [
        {
          value: {
            _id: currentUser.value?._id,
            description: `${currentUser.value?.firstName} ${currentUser.value?.lastName}`.trim(),
          },
          description: `${currentUser.value?.firstName} ${currentUser.value?.lastName}`.trim(),
        },
      ]
    }

    const assignableUsers = await userStore.getAssignableUsers(internalValue.value.supplier._id)

    // add current user if he has access to the organization but is not assignable
    if (
      currentUser.value?.organization === internalValue.value.supplier._id &&
      !hasScope(internalValue.value.organization!, AuthScope.CAN_BE_ASSIGNED)
    ) {
      assignableUsers.push({
        ...pick(currentUser.value, ["_id", "email", "hasAccess", "userRole", "firstName", "lastName", "organization"]),
        description: `${currentUser.value?.firstName} ${currentUser.value?.lastName}`.trim(),
      })
    }

    return sortBy(
      assignableUsers.map((user) => ({
        value: { _id: user._id, description: `${user.firstName} ${user.lastName}`.trim() },
        description: `${user.firstName} ${user.lastName}`.trim(),
      })),
      "description",
    )
  },
  { default: () => [] as Array<{ value: DocumentDescription; description: string }>, watch: [isReadOnly] },
)

const {
  data: invoicePeriods,
  pending: invoicePeriodsPending,
  refresh: refreshInvoicePeriods,
} = useLazyAsyncData(
  async () => {
    if (isReadOnly.value || !hasOrganization.value) return []

    const query = {
      pagination: false,
      query: {
        // We need to filter on organization here, otherwise suppliers will
        // receive all invoice periods from all organizations they have access to
        organization: internalValue.value.organization,
      },
      select: {
        _id: 1,
        description: 1,
      },
      sort: {
        createdAt: -1,
      },
    }

    const { docs } = await invoicePeriodStore.getByPage(query)
    return docs
  },
  { default: () => [] as Array<ListValue>, watch: [isReadOnly] },
)

const debounceDescription = debounce((value: string) => {
  internalValue.value = {
    ...internalValue.value,
    description: value,
  }
}, DEFAULT_DEBOUNCE_TIME)

const debounceRemark = debounce((value: string) => {
  internalValue.value = {
    ...internalValue.value,
    remark: value,
  }
}, DEFAULT_DEBOUNCE_TIME)

const cleanTicketValue = (
  rawTicket: Partial<Action> | Partial<Malfunction> | Partial<Inspection> | Partial<Nen3140> | Partial<Nen1010>,
): Partial<Action> | Partial<Malfunction> | Partial<Inspection> | Partial<Nen3140> | Partial<Nen1010> => ({
  // only keep properties that actually get modified by the form
  _id: rawTicket._id,
  asset: rawTicket.asset,
  description: rawTicket.description,
  date: toIsoDate(rawTicket.date) || undefined,
  supplier: rawTicket.supplier,
  users: rawTicket.users,
  contract: rawTicket.contract,
  invoicePeriod: rawTicket.invoicePeriod,
  remark: rawTicket.remark,
  organization: rawTicket.organization,
  articles: rawTicket.articles,
  tags: rawTicket.tags,
})

const onOrganizationUpdated = async () => {
  internalValue.value.asset = undefined
  internalValue.value.contract = undefined
  internalValue.value.articles = undefined

  refreshDescriptions()
  refreshSuppliers()
  refreshInvoicePeriods()
}

watch(
  () => cleanTicketValue(modelValue.value),
  async (cleanModelValue) => {
    if (!cleanModelValue) return

    if (!isEqual(cleanModelValue.supplier, internalValue.value.supplier)) {
      await refreshSuppliers()
    }

    internalValue.value = cleanTicketValue(cleanModelValue)
  },
  { deep: true, immediate: true },
)

watch(
  internalValue,
  () => {
    if (isEqual(cleanTicketValue(internalValue.value), cleanTicketValue(modelValue.value))) return

    emit("update:modelValue", {
      ...modelValue.value,
      ...cleanTicketValue(internalValue.value),
    })
  },
  { deep: true },
)

watch(
  () => internalValue.value.supplier,
  (val, oldVal) => {
    if (!isEqual(val, oldVal)) refreshInspectors()
  },
)

watch(
  ticketType,
  (val, oldVal) => {
    if (!isEqual(val, oldVal)) refreshDescriptions()
  },
  { deep: true },
)
</script>

<!-- Only works on input with scoped styles -->
<style scoped>
@media (min-width: 960px) {
  .small-field-text :deep(.v-field__input),
  .small-field-text :deep(.v-field__input input),
  .small-field-text :deep(.v-field__input input::placeholder) {
    font-size: 0.875rem !important;
  }
}
</style>
